import { useEffect } from "react";
import { Link } from "react-router-dom";
import { NavHeader } from "../components/NavHeader";
import { Footer } from "../components/Footer";
import { Disclaimer } from "../components/Disclaimer";
import { Sparkles } from "lucide-react";

const STEPS = [
  {
    n: "01",
    title: "You bring three things.",
    body:
      "Your name, your date of birth, and your place of birth. Time of birth is optional but recommended. With it, the reading can gesture at your rising sign. Without it, we stay anchored to your sun sign, element, modality and place.",
  },
  {
    n: "02",
    title: "We anchor your chart.",
    body:
      "Your sun sign, element (fire, earth, air, water), modality (cardinal, fixed, mutable) and ruling planet are computed from your date of birth using the traditional tropical-zodiac date ranges. This part is deterministic and rooted in centuries of astrological practice.",
  },
  {
    n: "03",
    title: "The almanac is composed.",
    body:
      "Seven chapters are written specifically for you: career, love, family, the year ahead, wealth, health, and strengths and shadows. Each chapter is rendered from your anchors, your place of birth, and the date you arrived. No two readings are alike.",
  },
  {
    n: "04",
    title: "A favourable forecast is attached.",
    body:
      "Day, colour, number, gemstone, direction, compatible signs, and a one-line power phrase, drawn from the planetary rulership of your sign. Useful as a quiet daily anchor.",
  },
  {
    n: "05",
    title: "You can ask anything.",
    body:
      "Once your almanac is up, the Ask the Wheel panel lets you put any question to your chart. Love, work, family, timing, decisions you have been putting off. Answers are short, specific, and rooted in the same anchors as the almanac.",
  },
  {
    n: "06",
    title: "Share, or step away.",
    body:
      "If you want to send your reading to someone, tap Share my almanac. We mint a private, unguessable link that lives for seven days and then disappears. No account required to view it. No account required to make it.",
  },
];

export const HowItWorksPage = () => {
  useEffect(() => {
    document.title = "How it works · Astrarium";
  }, []);
  return (
    <>
      <NavHeader />
      <main
        data-testid="how-it-works-page"
        className="relative z-10 max-w-3xl mx-auto px-4 sm:px-6 pt-28 sm:pt-32 pb-12"
      >
        <p className="text-stardust/70 uppercase tracking-[0.5em] text-[10px] mb-5 text-center">From your birthday to your almanac</p>
        <h1 className="font-display text-4xl sm:text-6xl text-[#F5EBD8] text-center leading-tight mb-5">
          Six steps, one almanac<span className="text-stardust">.</span>
        </h1>
        <p className="font-serif-italic text-slate-300/90 text-lg sm:text-2xl leading-relaxed text-center mb-16 max-w-2xl mx-auto">
          A reading takes about two minutes to begin and another minute to compose.
          Here is exactly what happens in between.
        </p>

        <ol className="space-y-10 sm:space-y-12">
          {STEPS.map((s) => (
            <li key={s.n} className="flex gap-5 sm:gap-7" data-testid={`step-${s.n}`}>
              <div className="font-display text-3xl sm:text-4xl text-stardust shrink-0 leading-none">
                {s.n}
              </div>
              <div>
                <h2 className="font-display text-2xl sm:text-3xl text-[#F5EBD8] mb-3 leading-tight">
                  {s.title}
                </h2>
                <p className="text-slate-300 leading-relaxed text-base sm:text-lg">{s.body}</p>
              </div>
            </li>
          ))}
        </ol>

        <div className="mt-16 text-center">
          <Link
            to="/"
            data-testid="how-it-works-cta"
            className="inline-flex items-center bg-stardust hover:bg-stardust-light text-cosmic-900 font-medium uppercase tracking-[0.3em] text-[11px] px-10 py-5 rounded-md shadow-[0_0_40px_rgba(226,194,117,0.25)] hover:shadow-[0_0_55px_rgba(226,194,117,0.45)] transition-all"
          >
            <Sparkles className="h-4 w-4 mr-3" />
            Begin a reading
          </Link>
        </div>

        <Disclaimer />
      </main>
      <Footer />
    </>
  );
};
